// Knowledge Base — mock API backing (Phase 1 read path).
// Spec: 20260706-portal-knowledge-base §6.5 (read contract).
//
// Implements the SAME method surface as the real `kbRealApi` in apiClient.js and
// resolves with the SAME axios-like `{ data }` envelope, so call sites are
// identical whether they hit fixtures or the live CARO-Api. Flip
// `USE_KB_MOCKS` in apiClient.js (or set REACT_APP_KB_USE_MOCKS=false) to swap.
//
// The read endpoints this stands in for (all JWT-gated, any role):
//   KB_PAGES · KB_PAGE(slug) · KB_SEARCH_INDEX(locale) · KB_STATUS · KB_RUNS

import {
  KB_PAGES_FIXTURE,
  KB_RUN_FIXTURE,
  KB_RUNS_FIXTURE,
  KB_REVISIONS_FIXTURE,
  kbNextAssetId,
  kbNextPageId,
  kbNextContentId,
  kbNextRevisionId,
} from './kbFixtures';

// Body-prose locales generated in v1 (LD-LANG). Others fall back to `en`.
const BODY_LOCALES = ['en', 'fr', 'nl'];
const normalizeLocale = (locale) => (BODY_LOCALES.includes(locale) ? locale : 'en');

// Small artificial latency so loading states are exercised in the UI.
const respond = (data, ms = 120) =>
  new Promise((resolve) => setTimeout(() => resolve({ data }), ms));

// Pick the localized page title with `en` fallback.
const localizedTitle = (page, locale) => {
  const want = normalizeLocale(locale);
  const row =
    (page.content || []).find((c) => c.locale === want) ||
    (page.content || []).find((c) => c.locale === 'en');
  return (row && row.title) || page.title;
};

// Effective section text for search indexing (override ?? base, `en` fallback).
const sectionText = (section, locale) => {
  const want = normalizeLocale(locale);
  const row =
    (section.content || []).find((c) => c.locale === want) ||
    (section.content || []).find((c) => c.locale === 'en');
  if (!row) return { heading: '', text: '' };
  return { heading: row.heading || '', text: row.overrideMd || row.baseMd || '' };
};

// Build a compact per-locale search index — the same `{slug,title,headings[],
// text,kind}` shape the generator would emit (§5.6).
const buildSearchIndex = (locale) =>
  KB_PAGES_FIXTURE.map((page) => {
    const want = normalizeLocale(locale);
    const introRow =
      (page.content || []).find((c) => c.locale === want) ||
      (page.content || []).find((c) => c.locale === 'en');
    const headings = [];
    const bodyParts = [introRow ? introRow.introMd || '' : ''];
    (page.sections || []).forEach((s) => {
      const { heading, text } = sectionText(s, locale);
      if (heading) headings.push(heading);
      if (text) bodyParts.push(text);
    });
    return {
      slug: page.slug,
      title: localizedTitle(page, locale),
      kind: page.kind,
      headings,
      // Strip markdown punctuation to keep the index lean; FlexSearch tokenizes.
      text: bodyParts.join(' \n ').replace(/[#*_>`|-]/g, ' '),
    };
  });

// ── Write-side helpers (Phase 2 — authoring / override / revisions) ──────────
// These mutate the in-memory fixture arrays so the admin console works fully
// offline. Each mirrors what the CARO-Api write endpoint (§6.5, 2a) will do
// server-side; the method signatures are byte-for-byte identical to the
// `kbRealApi` write methods so mock→real is a pure flag flip.

const findPage = (id) => KB_PAGES_FIXTURE.find((p) => String(p.id) === String(id));
const findPageBySlug = (slug) => KB_PAGES_FIXTURE.find((p) => p.slug === slug);
const findSection = (sectionId) => {
  for (const page of KB_PAGES_FIXTURE) {
    const section = (page.sections || []).find((s) => String(s.id) === String(sectionId));
    if (section) return { page, section };
  }
  return { page: null, section: null };
};

// Assemble a full effective-Markdown snapshot for a page (English), used as the
// revision `snapshot_md` (§4.3). Intro + each section's effective prose.
const snapshotOf = (page) => {
  const parts = [];
  const intro =
    (page.content || []).find((c) => c.locale === 'en') || (page.content || [])[0];
  if (intro) parts.push(intro.overrideMd || intro.introMd || '');
  (page.sections || []).forEach((s) => {
    const row = (s.content || []).find((c) => c.locale === 'en') || (s.content || [])[0];
    if (!row) return;
    if (row.heading) parts.push(`## ${row.heading}`);
    parts.push(row.overrideMd || row.baseMd || '');
  });
  return parts.filter(Boolean).join('\n\n');
};

// Append an immutable revision row for a page (append-only, LD-REVISIONS).
const appendRevision = (page, origin, summary) => {
  const list = KB_REVISIONS_FIXTURE[page.id] || (KB_REVISIONS_FIXTURE[page.id] = []);
  const revisionNo = list.length ? list[list.length - 1].revisionNo + 1 : 1;
  const rev = {
    id: kbNextRevisionId(),
    pageId: page.id,
    revisionNo,
    origin,
    snapshotMd: snapshotOf(page),
    summary: summary || null,
    authorId: origin === 'admin' ? 7 : null,
    runId: null,
    createdAt: new Date().toISOString(),
  };
  list.push(rev);
  return rev;
};

// Upsert per-locale content rows on a page from `[{locale,title,introMd}]`.
const applyPageContent = (page, content) => {
  if (!Array.isArray(content)) return;
  page.content = page.content || [];
  content.forEach((incoming) => {
    if (!incoming || !incoming.locale) return;
    let row = page.content.find((c) => c.locale === incoming.locale);
    if (!row) {
      row = { id: kbNextContentId(), pageId: page.id, locale: incoming.locale };
      page.content.push(row);
    }
    if ('title' in incoming) row.title = incoming.title;
    if ('introMd' in incoming) row.introMd = incoming.introMd;
  });
};

export const kbMockApi = {
  // GET /api/v1/kb/pages?locale= — localized browse list.
  listPages: (locale) =>
    respond(
      KB_PAGES_FIXTURE.map((p) => ({
        id: p.id,
        uuid: p.uuid,
        slug: p.slug,
        pageKey: p.pageKey,
        source: p.source,
        kind: p.kind,
        title: localizedTitle(p, locale),
        routePath: p.routePath,
        locked: p.locked,
        stale: p.stale,
        lastError: p.lastError || null,
        lastGeneratedAt: p.lastGeneratedAt || null,
      })),
    ),

  // GET /api/v1/kb/pages/:slug?locale= — one fully-hydrated page (all locales,
  // so the portal resolves the active locale + `en` fallback itself).
  getPage: (slug /* , locale */) => {
    const page = KB_PAGES_FIXTURE.find((p) => p.slug === slug);
    if (!page) {
      const err = new Error(`KB mock: page '${slug}' not found`);
      err.response = { status: 404 };
      return Promise.reject(err);
    }
    return respond(page);
  },

  // GET /api/v1/kb/search-index?locale= — prebuilt per-locale index JSON.
  getSearchIndex: (locale) => respond(buildSearchIndex(locale)),

  // GET /api/v1/kb/status — latest run + per-page freshness.
  getStatus: () =>
    respond({
      latestRun: KB_RUN_FIXTURE,
      lastSucceededAt: KB_RUN_FIXTURE.finishedAt,
      pages: KB_PAGES_FIXTURE.map((p) => ({
        slug: p.slug,
        title: p.title,
        source: p.source,
        stale: p.stale,
        locked: p.locked,
        lastError: p.lastError || null,
        lastGeneratedAt: p.lastGeneratedAt || null,
      })),
    }),

  // GET /api/v1/kb/runs — run history.
  getRuns: () => respond(KB_RUNS_FIXTURE),

  // ── Write side (knowledge_base.manage) ────────────────────────────────────

  // POST /api/v1/kb/pages — create a manual page. Appends an initial revision.
  createPage: (payload) => {
    const p = payload || {};
    const slug = String(p.slug || '').trim();
    if (!slug) {
      const err = new Error('KB mock: slug is required');
      err.response = { status: 400 };
      return Promise.reject(err);
    }
    if (findPageBySlug(slug)) {
      const err = new Error(`KB mock: slug '${slug}' already exists`);
      err.response = { status: 409 };
      return Promise.reject(err);
    }
    const id = kbNextPageId();
    const enRow = (p.content || []).find((c) => c.locale === 'en') || (p.content || [])[0];
    const page = {
      id,
      uuid: `kb-page-${String(id).padStart(4, '0')}`,
      slug,
      pageKey: null,
      source: 'manual',
      kind: p.kind === 'action' ? 'action' : 'screen',
      title: (enRow && enRow.title) || slug,
      routePath: null,
      locked: false,
      stale: false,
      lastGeneratedAt: null,
      content: [],
      sections: [],
    };
    applyPageContent(page, p.content);
    KB_PAGES_FIXTURE.push(page);
    appendRevision(page, 'admin', 'admin edit — page created');
    return respond(page);
  },

  // PATCH /api/v1/kb/pages/:id — edit a manual page / page-level override.
  updatePage: (id, payload) => {
    const page = findPage(id);
    if (!page) {
      const err = new Error(`KB mock: page '${id}' not found`);
      err.response = { status: 404 };
      return Promise.reject(err);
    }
    const p = payload || {};
    applyPageContent(page, p.content);
    if (typeof p.title === 'string') page.title = p.title;
    const enRow = (page.content || []).find((c) => c.locale === 'en');
    if (enRow && enRow.title) page.title = enRow.title;
    appendRevision(page, 'admin', 'admin edit — page updated');
    return respond(page);
  },

  // PUT /api/v1/kb/sections/:id/override — set locale override + lock (US-7).
  overrideSection: (sectionId, body) => {
    const { page, section } = findSection(sectionId);
    if (!section) {
      const err = new Error(`KB mock: section '${sectionId}' not found`);
      err.response = { status: 404 };
      return Promise.reject(err);
    }
    const { locale, overrideMd } = body || {};
    const want = normalizeLocale(locale);
    section.content = section.content || [];
    let row = section.content.find((c) => c.locale === want);
    if (!row) {
      row = { id: kbNextContentId(), sectionId: section.id, locale: want };
      section.content.push(row);
    }
    row.overrideMd = overrideMd;
    // Lock spans the whole section across all locales (§7); a fresh override
    // clears the stale flag until the next run re-diffs the evidence.
    section.locked = true;
    section.stale = false;
    appendRevision(page, 'admin', `admin edit — override ${section.kbElementId} (${want})`);
    return respond(section);
  },

  // POST /api/v1/kb/sections/:id/revert — clear override + lock (US-9).
  revertSection: (sectionId) => {
    const { page, section } = findSection(sectionId);
    if (!section) {
      const err = new Error(`KB mock: section '${sectionId}' not found`);
      err.response = { status: 404 };
      return Promise.reject(err);
    }
    (section.content || []).forEach((c) => {
      c.overrideMd = null;
    });
    section.locked = false;
    section.stale = false;
    appendRevision(page, 'admin', `reverted ${section.kbElementId} to auto`);
    return respond(section);
  },

  // GET /api/v1/kb/pages/:id/revisions — append-only history (newest first).
  listRevisions: (pageId) => {
    const list = KB_REVISIONS_FIXTURE[pageId] || [];
    return respond([...list].sort((a, b) => b.revisionNo - a.revisionNo));
  },

  // POST /api/v1/kb/pages/:id/revert/:rev — roll back to a revision (US-13).
  // Append-only: writes the chosen snapshot as a NEW revision + updates live.
  revertToRevision: (pageId, rev) => {
    const page = findPage(pageId);
    const list = KB_REVISIONS_FIXTURE[pageId] || [];
    const target = list.find((r) => String(r.revisionNo) === String(rev));
    if (!page || !target) {
      const err = new Error('KB mock: page or revision not found');
      err.response = { status: 404 };
      return Promise.reject(err);
    }
    // For a manual page, re-adopt the snapshot as the English body so the
    // rollback is visible on the rendered page.
    if (page.source === 'manual') {
      applyPageContent(page, [{ locale: 'en', introMd: target.snapshotMd }]);
    }
    const newRev = appendRevision(page, 'admin', `rolled back to revision ${target.revisionNo}`);
    return respond({ page, revision: newRev });
  },

  // POST /api/v1/kb/assets — image upload. Returns an auth-proxied-style URL.
  // Mock: reads the File as a data-URI so the inserted Markdown image renders
  // in the live preview with no file server. Real API returns a KB_ASSET URL.
  uploadAsset: (file) => {
    const uuid = `kb-asset-${kbNextAssetId()}`;
    const done = (url) => respond({ uuid, url, kind: 'upload', mime: (file && file.type) || 'image/png' }, 300);
    if (typeof FileReader !== 'undefined' && file instanceof Blob) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => done(String(reader.result)).then(resolve, reject);
        reader.onerror = () => reject(reader.error || new Error('KB mock: read failed'));
        reader.readAsDataURL(file);
      });
    }
    // Fallback placeholder when no real File is provided.
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="480" height="240"><rect width="480" height="240" fill="#efe6ec"/><text x="240" y="125" font-family="system-ui" font-size="16" fill="#9A3F69" text-anchor="middle">uploaded image (mock)</text></svg>`;
    return done(`data:image/svg+xml;utf8,${encodeURIComponent(svg)}`);
  },
};
